import React, { useEffect, useMemo, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import Tooltip from './Tooltip';
import { useClub } from '../context/ClubContext';
import { formatSportDisplayName } from '../utils/sportName';
import { getCourtDisplayNameFromCourt } from '../utils/courtDisplay';

const Calendar = ({ bookings, selectedCourt, onSlotClick, currentDate, onPreviousWeek, onNextWeek, isLoading, onCourtSelect, onBookingSelect, courts, localTimeSlots, selectedSport, user, onShareBooking }) => {
  const { getCategoryStyle, courtData, getIconBackgroundColor } = useClub();
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getStartOfWeek = (date) => {
    date.setHours(0, 0, 0, 0); // Set to midnight
    return date;
  };

  const startOfWeek = getStartOfWeek(new Date(currentDate));

  const isCurrentWeekInPast = () => {
    const now = new Date();
    // Get the current time in UTC
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000)); // Adjust to UTC
    // Adjust to South Africa time (UTC+2)
    const southAfricaTime = new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
    southAfricaTime.setHours(0, 0, 0, 0);
    return startOfWeek < southAfricaTime;
  };

  const getCurrentClubTime = () => {
    const now = new Date();
    const utcTime = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
    return new Date(utcTime.getTime() + (2 * 60 * 60 * 1000));
  };

  const handleMouseDown = (date, localTime) => {
    setIsSelecting(true);
    setSelectedSlots([{ court: selectedCourt, date, localTime }]);
  };

  const handleMouseEnter = (date, localTime) => {
    if (isSelecting) {
      setSelectedSlots((prev) => {
        const lastSlot = prev[prev.length - 1];
        if (lastSlot && lastSlot.court === selectedCourt && lastSlot.date === date) {
          return [...prev, { court: selectedCourt, date, localTime }];
        }
        return prev;
      });
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
    if (selectedSlots.length > 0) {
      // Sort selected slots to find the earliest and latest times
      const sortedSlots = selectedSlots.sort((a, b) => {
        const aTime = new Date(`${a.date}T${a.localTime}:00`);
        const bTime = new Date(`${b.date}T${b.localTime}:00`);
        return aTime - bTime; // Sort by time
      });

      const startTime = sortedSlots[0].localTime; // First slot's time
      const endTime = sortedSlots[sortedSlots.length - 1].localTime; // Last slot's time
      const [endHour, endMinute] = endTime.split(':').map(Number);

      // Get the slot duration for the selected court
      const selectedCourtData = courtData.find(court => court.court === selectedSlots[0].court);
      const slotDuration = selectedCourtData ? parseInt(selectedCourtData.slotDuration, 10) : 30; // Default to 30 if not found

      // Calculate adjusted end time based on slot duration
      const adjustedEndTime = new Date();
      adjustedEndTime.setHours(endHour, endMinute, 0, 0);
      adjustedEndTime.setMinutes(adjustedEndTime.getMinutes() + slotDuration); // Increment by slot duration

      const booking = getBookingDetails(selectedSlots[0].court, selectedSlots[0].date, startTime);
      
      if (booking) {
        onBookingSelect(booking);
      } else {
        onSlotClick({
          court: selectedSlots[0].court,
          date: selectedSlots[0].date,
          startTime,
          endTime: adjustedEndTime.toTimeString().slice(0, 5) // Format as HH:MM
        });
      }
      
      setSelectedSlots([]);
    }
  };

  const isSlotSelected = (date, localTime) => {
    return selectedSlots.some(slot => slot.court === selectedCourt && slot.date === date && slot.localTime === localTime);
  };

  const parseTimeToMinutes = (time) => {
    const [hour, minute] = time.split(':').map(Number);
    return (hour * 60) + minute;
  };

  const isBlockBooking = (booking) => {
    if (booking.bookingDate === 'BLOCK') return true;
    if (!booking.isRecurring || !booking.recurringRule) return false;
    try {
      const recurringRule = typeof booking.recurringRule === 'string'
        ? JSON.parse(booking.recurringRule)
        : booking.recurringRule;
      return recurringRule?.duration === -1;
    } catch {
      return false;
    }
  };

  const bookingKey = (booking) => `${booking.bookingId || `${booking.court}-${booking.bookingDate}-${booking.startTime}-${booking.endTime}`}`;

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => onNextWeek(), // Always allow swipe left (forward)
    onSwipedRight: () => {
      if (!isCurrentWeekInPast()) { // Only allow swipe right (backwards) if not in the past
        onPreviousWeek();
      }
    },
    trackMouse: false, // optional: Not suitable for Dektop mouse since click will open booking modal
    preventScrollOnSwipe: true, // Need to be able to scroll up and down to view time slots
    delta: 50, // minimum distance in px before swipe is triggered
  });

  // Filter courts based on selected sport
  const filteredCourts = !selectedSport || selectedSport === 'All Courts' 
    ? courts 
    : courts.filter(court => court.court.startsWith(selectedSport));

  // Group filtered courts by sport type dynamically
  const courtsBySport = filteredCourts.reduce((acc, court) => {
    const sport = court.court.split('-')[0];
    if (!acc[sport]) {
      acc[sport] = [];
    }
    acc[sport].push(court);
    return acc;
  }, {});
  
  const sportTypes = (() => {
    const keys = Object.keys(courtsBySport);
    if (keys.length === 0) return keys;
    const sportIndexes = courts.reduce((acc, court) => {
      const sport = court.court.split('-')[0];
      const index = Number(court.sportIndex);
      if (!Number.isFinite(index)) return acc;
      if (acc[sport] === undefined || index < acc[sport]) {
        acc[sport] = index;
      }
      return acc;
    }, {});
    return [...keys].sort((a, b) => {
      const aIndex = sportIndexes[a];
      const bIndex = sportIndexes[b];
      if (aIndex === undefined && bIndex === undefined) {
        return a.localeCompare(b);
      }
      if (aIndex === undefined) return 1;
      if (bIndex === undefined) return -1;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return a.localeCompare(b);
    });
  })();

  const daysToRender = isSmallScreen ? 3 : 7;
  const gridColsClass = isSmallScreen ? 'grid-cols-3' : 'grid-cols-7';
  const gridTemplateColumns = `repeat(${daysToRender}, minmax(0, 1fr))`;
  const gridTemplateRows = `repeat(${localTimeSlots.length}, minmax(40px, auto))`;

  const daysOfWeek = useMemo(() => {
    const base = getStartOfWeek(new Date(currentDate));
    return Array.from({ length: daysToRender }, (_, i) => {
      const date = new Date(base);
      date.setDate(base.getDate() + i);
      return date;
    });
  }, [currentDate, daysToRender]);

  const dayDateStrings = useMemo(
    () => daysOfWeek.map((date) => date.toLocaleDateString('en-CA').split('T')[0]),
    [daysOfWeek]
  );

  const { slotBookingMap, segmentStartMap } = useMemo(() => {
    const slotMap = new Map();
    const startMap = new Map();
    if (!selectedCourt) {
      return { slotBookingMap: slotMap, segmentStartMap: startMap };
    }

    dayDateStrings.forEach((dateString) => {
      const dayBookings = bookings.filter(
        (b) => b.court === selectedCourt && b.bookingDate === dateString
      );
      const regularBookings = dayBookings.filter((b) => !isBlockBooking(b));
      const blockBookings = dayBookings.filter((b) => isBlockBooking(b));
      const orderedBookings = [...regularBookings, ...blockBookings];

      orderedBookings.forEach((booking) => {
        const bookingStart = parseTimeToMinutes(booking.startTime);
        const bookingEnd = parseTimeToMinutes(booking.endTime);

        localTimeSlots.forEach((slot) => {
          const slotMin = parseTimeToMinutes(slot);
          if (slotMin < bookingStart || slotMin >= bookingEnd) return;

          const mapKey = `${dateString}#${slot}`;
          if (!slotMap.has(mapKey)) {
            slotMap.set(mapKey, booking);
          }
        });
      });

      let index = 0;
      while (index < localTimeSlots.length) {
        const startSlot = localTimeSlots[index];
        const startBooking = slotMap.get(`${dateString}#${startSlot}`);
        if (!startBooking) {
          index += 1;
          continue;
        }

        let span = 1;
        const startBookingId = bookingKey(startBooking);
        while (index + span < localTimeSlots.length) {
          const nextSlot = localTimeSlots[index + span];
          const nextBooking = slotMap.get(`${dateString}#${nextSlot}`);
          if (!nextBooking || bookingKey(nextBooking) !== startBookingId) break;
          span += 1;
        }

        startMap.set(`${dateString}#${startSlot}`, {
          booking: startBooking,
          span
        });

        index += span;
      }
    });

    return { slotBookingMap: slotMap, segmentStartMap: startMap };
  }, [bookings, dayDateStrings, localTimeSlots, selectedCourt]);

  const getBookingDetails = (court, date, localTime) => {
    if (court !== selectedCourt) return null;
    return slotBookingMap.get(`${date}#${localTime}`) || null;
  };

  const renderCalendarContent = () => {
    const currentTime = getCurrentClubTime();

    return (
      <>
        <div className="shadow-md rounded-t-lg" {...swipeHandlers}>
          {/* Sticky header row with navigation arrows and court selection */}
          <div className="sticky top-0 z-20 bg-white rounded-t-lg">
            {/* Court selection buttons */}
            <div className="flex flex-col gap-2 py-2 border-b border-slate-400">
              {sportTypes.map((sport) => (
                <div key={sport} className="flex justify-around items-center">
                  {courtsBySport[sport].map((court) => (
                    <button
                      key={court.court}
                      onClick={() => onCourtSelect(court.court)}
                      type="button"
                      className={`flex flex-1 min-w-0 items-center justify-center mx-1 text-xs sm:text-sm md:text-base py-1 px-0.5 rounded border overflow-hidden ${
                        selectedCourt === court.court
                          ? getCategoryStyle({
                              category: 'Available',
                              court: court.court
                            })
                          : 'bg-slate-200 text-slate-800 border-transparent'
                      }`}
                      style={{ flexBasis: '0', flexGrow: '1', minWidth: 0 }}
                    >
                      {(() => {
                        const aliasLabel = getCourtDisplayNameFromCourt(court, '').trim();
                        if (aliasLabel) {
                          return (
                            <div className="flex w-full min-w-0 max-w-full justify-center">
                              <div
                                className="max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                                title={aliasLabel}
                              >
                                {aliasLabel}
                              </div>
                            </div>
                          );
                        }
                        const sportName = formatSportDisplayName(court.court.split('-')[0]);
                        const courtNumber = court.court.split('-')[1];
                        const isSingleSportInView = sportTypes.length === 1;
                        const fullLabel = isSingleSportInView
                          ? `Court ${courtNumber}`
                          : `${sportName} ${courtNumber}`;
                        if (isSingleSportInView) {
                          return (
                            <div className="flex w-full min-w-0 max-w-full justify-center">
                              <div
                                className="flex max-w-full min-w-0 items-baseline gap-0.5"
                                title={fullLabel}
                              >
                                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                                  Court
                                </span>
                                <span className="shrink-0 tabular-nums">{courtNumber}</span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="flex w-full min-w-0 max-w-full justify-center">
                            <div
                              className="flex max-w-full min-w-0 items-baseline gap-0.5"
                              title={fullLabel}
                            >
                              <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                                {sportName}
                              </span>
                              <span className="shrink-0 tabular-nums">{courtNumber}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            <div className={`grid ${gridColsClass} border-b border-slate-400 items-center`}>
              {/* Left nav arrow */}
              <div className="absolute left-2 flex items-center -ml-3 z-10">
                {isSmallScreen ? (
                  <button
                    onClick={onPreviousWeek}
                    disabled={isCurrentWeekInPast()}
                    className={`p-2 rounded transition ${
                      isCurrentWeekInPast()
                        ? 'text-slate-300 cursor-not-allowed'
                        : 'text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                ) : (
                  <Tooltip content="Previous">
                    <button
                      onClick={onPreviousWeek}
                      disabled={isCurrentWeekInPast()}
                      className={`p-2 rounded transition ${
                        isCurrentWeekInPast()
                          ? 'text-slate-300 cursor-not-allowed'
                          : 'text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  </Tooltip>
                )}
              </div>

              {/* Day headers */}
              {daysOfWeek.map((date, index) => {
                return (
                  <div
                    key={index}
                    className={`p-1 text-center text-xs sm:text-sm font-medium text-slate-800 relative flex flex-col ${
                      index < daysOfWeek.length - 1 ? 'border-r border-slate-200' : ''
                    }`}
                  >
                    <span>{date.toLocaleString('default', { weekday: 'short' })}</span>
                    <span className="text-xs text-slate-800">
                      {date.getDate()} {date.toLocaleString('default', { month: 'short' })}
                    </span>
                  </div>
                );
              })}

              {/* Right nav arrow */}
              <div className="absolute right-2 items-center -mr-3 z-10">
                {isSmallScreen ? (
                  <button
                    onClick={onNextWeek}
                    className="p-2 rounded text-slate-600 hover:bg-slate-200 transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <Tooltip content="Next">
                    <button
                      onClick={onNextWeek}
                      className="p-2 rounded text-slate-600 hover:bg-slate-200 transition"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>

          {/* Time slot grid */}
          <div
            className={`grid ${isLoading ? 'opacity-50 pointer-events-none' : ''} bg-white`}
            style={{
              gridTemplateColumns,
              gridTemplateRows,
              minWidth: 0,
              overflow: 'hidden'
            }}
          >
            {daysOfWeek.map((date, dayIndex) => {
              const formattedDate = date.toLocaleDateString('en-CA').split('T')[0];

              return localTimeSlots.map((localTime, rowIndex) => {
                const slotKey = `${formattedDate}#${localTime}`;
                const booking = slotBookingMap.get(slotKey) || null;
                const segment = segmentStartMap.get(slotKey);

                if (booking && !segment) {
                  return null;
                }

                const [hour, minute] = localTime.split(':').map(Number);
                const slotTime = new Date(formattedDate);
                slotTime.setHours(hour, minute, 0, 0);

                let disablePastInteraction = slotTime < currentTime;
                if (booking) {
                  const bookingEnd = new Date(`${booking.bookingDate}T${booking.endTime}:00`);
                  disablePastInteraction = bookingEnd < currentTime;
                }

                const isBookable = !booking && slotTime >= currentTime;
                const canSelectSlot = !booking && !disablePastInteraction;
                const rowSpan = segment?.span || 1;
                const allowWrappedLabel = Boolean(booking) && rowSpan >= 3;
                const showCenteredPublicIcon = Boolean(booking?.public) && rowSpan >= 3;
                const canShowShareIcon = Boolean(booking?.public) && !disablePastInteraction;

                const categoryClasses = getCategoryStyle({ category: booking?.category, court: selectedCourt }).split(' ');
                const recurringBorderL = booking?.isRecurring
                  ? getCategoryStyle({ category: 'Recurring', court: selectedCourt }).split(' ').find(cls => cls.startsWith('border-l-'))
                  : '';
                const filteredCategoryClasses = booking?.isRecurring
                  ? categoryClasses.filter(cls => !cls.startsWith('border-l-')).join(' ')
                  : categoryClasses.join(' ');

                const categoryBorderColor = categoryClasses.find(cls => cls.startsWith('border-') && !cls.startsWith('border-l-')) || 'border-blue-400';
                const iconBgColor = getIconBackgroundColor(categoryBorderColor);

                return (
                  <div
                    key={`${formattedDate}-${localTime}`}
                    className={`min-h-[calendar] p-1 border-r border-b border-slate-100 ${canSelectSlot && isSlotSelected(formattedDate, localTime) ? 'hover:bg-booking-hover-available' : ''}`}
                    style={{
                      minWidth: 0,
                      overflow: 'hidden',
                      gridColumn: dayIndex + 1,
                      gridRow: `${rowIndex + 1} / span ${rowSpan}`,
                      zIndex: booking ? 10 : 1
                    }}
                    onMouseDown={() => canSelectSlot && handleMouseDown(formattedDate, localTime)}
                    onMouseEnter={() => canSelectSlot && handleMouseEnter(formattedDate, localTime)}
                    onMouseUp={handleMouseUp}
                  >
                    <button
                      className={`
                        w-full h-full min-w-0 p-1 rounded text-xs transition border relative
                        ${filteredCategoryClasses}
                        ${disablePastInteraction ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                        ${!booking && isSlotSelected(formattedDate, localTime) ? 'bg-emerald-100' : ''}
                        ${booking?.isRecurring ? 'border-l-4' : ''}
                        ${recurringBorderL}
                      `}
                      disabled={!booking && disablePastInteraction}
                      onClick={() => {
                        if (booking) {
                          onBookingSelect(booking);
                        }
                      }}
                    >
                      <span className="flex items-center justify-center gap-1 w-full min-w-0 overflow-hidden relative">
                        {booking && booking.status !== 'confirmed' && (
                          <span className="text-red-600 mr-1">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5">
                              <circle cx="12" cy="12" r="10" fill="currentColor" />
                              <rect x="11" y="6" width="2" height="8" rx="1" fill="white" />
                              <circle cx="12" cy="17" r="1.2" fill="white" />
                            </svg>
                          </span>
                        )}
                        {booking?.public && user && (() => {
                          try {
                            const publicInfo = typeof booking.public === 'string' ? JSON.parse(booking.public) : booking.public;
                            return publicInfo?.pending && publicInfo.pending[user.uuid];
                          } catch {
                            return false;
                          }
                        })() && (
                          <span className="text-red-600 mr-1">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5">
                              <circle cx="12" cy="12" r="10" fill="currentColor" />
                              <rect x="11" y="6" width="2" height="8" rx="1" fill="white" />
                              <circle cx="12" cy="17" r="1.2" fill="white" />
                            </svg>
                          </span>
                        )}
                        <span className="flex flex-col items-center w-full min-w-0 leading-tight">
                          <span
                            className={`block w-full min-w-0 text-center text-xs sm:text-sm md:text-base ${allowWrappedLabel ? 'whitespace-normal break-words' : 'overflow-hidden text-ellipsis whitespace-nowrap'}`}
                            style={allowWrappedLabel ? {
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            } : undefined}
                          >
                            {booking ? (booking.displayLabel || booking.createdByName) : ''}
                          </span>
                          {booking && rowSpan > 1 && (
                            <span className="block w-full min-w-0 text-center text-xs sm:text-xs md:text-xs mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
                              {`${booking.startTime} - ${booking.endTime}`}
                            </span>
                          )}
                          {showCenteredPublicIcon && (
                            <span className="mt-1 flex items-center justify-center gap-1">
                              <span className={`w-4 h-4 rounded-full ${iconBgColor} flex items-center justify-center`}>
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 512 512">
                                  <path fillRule="evenodd" clipRule="evenodd" d="M224,448c0,0-32,0-32-32s32-128,160-128s160,96,160,128s-32,32-32,32H224z M352,256c53,0,96-43,96-96s-43-96-96-96s-96,43-96,96S299,256,352,256z M166.9,448c-4.8-10-7.1-20.9-6.9-32c0-43.4,21.8-88,62-119c-20.1-6.2-41-9.2-62-9C32,288,0,384,0,416s32,32,32,32H166.9z M144,256c44.2,0,80-35.8,80-80s-35.8-80-80-80s-80,35.8-80,80S99.8,256,144,256z"/>
                                </svg>
                              </span>
                              {canShowShareIcon && (
                                <span className={`w-4 h-4 rounded-full ${iconBgColor} flex items-center justify-center`}>
                                  <svg
                                    className="w-3 h-3 text-white cursor-pointer"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      onShareBooking?.(booking);
                                    }}
                                  >
                                    <circle cx="18" cy="5" r="2"></circle>
                                    <circle cx="6" cy="12" r="2"></circle>
                                    <circle cx="18" cy="19" r="2"></circle>
                                    <path d="M8 12l8-6"></path>
                                    <path d="M8 12l8 6"></path>
                                  </svg>
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      </span>
                      {booking?.public && !showCenteredPublicIcon && (
                        <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${iconBgColor} flex items-center justify-center`}>
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 512 512">
                            <path fillRule="evenodd" clipRule="evenodd" d="M224,448c0,0-32,0-32-32s32-128,160-128s160,96,160,128s-32,32-32,32H224z M352,256c53,0,96-43,96-96s-43-96-96-96s-96,43-96,96S299,256,352,256z M166.9,448c-4.8-10-7.1-20.9-6.9-32c0-43.4,21.8-88,62-119c-20.1-6.2-41-9.2-62-9C32,288,0,384,0,416s32,32,32,32H166.9z M144,256c44.2,0,80-35.8,80-80s-35.8-80-80-80s-80,35.8-80,80S99.8,256,144,256z"/>
                          </svg>
                        </span>
                      )}
                      {canShowShareIcon && !showCenteredPublicIcon && (
                        <span
                          className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ${iconBgColor} flex items-center justify-center cursor-pointer`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onShareBooking?.(booking);
                          }}
                        >
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="2"></circle>
                            <circle cx="6" cy="12" r="2"></circle>
                            <circle cx="18" cy="19" r="2"></circle>
                            <path d="M8 12l8-6"></path>
                            <path d="M8 12l8 6"></path>
                          </svg>
                        </span>
                      )}
                      {!booking && (
                        <span className={`block text-xs sm:text-sm md:text-sm ${isBookable ? '' : 'invisible'}`}>
                          {localTime}
                        </span>
                      )}
                    </button>
                  </div>
                );
              });
            })}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {renderCalendarContent()}
    </>
  );
};

export default Calendar;
